import React from "react";
import { Alert, Input, Spin } from "antd";
import SectionHeader from "../../../common/section-header/index.js";
import CommonButton from "../../../common/button/button.js";
import Text from "../../../common/typography/text.js";

const ProductGroupsStep = ({
  productGroups = [],
  selectedIds = [],
  onToggleGroup,
  onToggleManufacturer,
  loading = false,
  error = "",
  hasActiveManufacturers = true,
  onRetry,
}) => {
  const [search, setSearch] = React.useState("");

  const selectedSet = React.useMemo(
    () => new Set(selectedIds.map(String)),
    [selectedIds],
  );

  const visibleGroups = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return productGroups;
    return productGroups.filter((group) =>
      [group.ProductGroupName, group.ManufacturerName, group.QCode]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [productGroups, search]);

  const manufacturers = React.useMemo(() => {
    const byManufacturer = new Map();
    visibleGroups.forEach((group) => {
      const key = String(group.ManufacturerID);
      if (!byManufacturer.has(key)) {
        byManufacturer.set(key, {
          ManufacturerID: group.ManufacturerID,
          ManufacturerName: group.ManufacturerName,
          groups: [],
        });
      }
      byManufacturer.get(key).groups.push(group);
    });
    return Array.from(byManufacturer.values());
  }, [visibleGroups]);

  if (loading) {
    return (
      <div className="account-enrollment-step">
        <div className="account-enrollment-empty" data-testid="product-groups-loading">
          <Spin />
          <Text color="lightText" variant="smallText">
            Loading product groups…
          </Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-enrollment-step">
        <Alert
          action={
            onRetry ? (
              <CommonButton onClick={onRetry} variant="link">
                Retry
              </CommonButton>
            ) : null
          }
          message={error}
          showIcon
          type="error"
        />
      </div>
    );
  }

  // Two different empty states (plan section 5): the distributor has no
  // active contract at all, or has manufacturers but none of their groups
  // is flagged Include on Enrollment Form. Both block advancing to step 3.
  if (productGroups.length === 0) {
    return (
      <div className="account-enrollment-step">
        <div className="account-enrollment-empty">
          {hasActiveManufacturers ? (
            <>
              <Text variant="bodyText">No product groups available for enrollment</Text>
              <Text color="lightText" variant="smallText">
                None of the product groups from this distributor&rsquo;s
                manufacturers are enabled for the enrollment form. An
                administrator must enable &ldquo;Include on Enrollment Form&rdquo;
                on at least one product group before an enrollment can be
                submitted.
              </Text>
            </>
          ) : (
            <>
              <Text variant="bodyText">No active manufacturer contracts</Text>
              <Text color="lightText" variant="smallText">
                This distributor has no active commission contract, so there are
                no manufacturers an enrollment can cover. A contract must be in
                place before an enrollment can be submitted.
              </Text>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="account-enrollment-step">
      <div className="account-enrollment-groups-toolbar">
        <Text color="lightText" variant="smallText">
          {selectedIds.length} group{selectedIds.length === 1 ? "" : "s"} selected
        </Text>
        <Input.Search
          allowClear
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search product groups"
          style={{ height: "36px", maxWidth: "320px" }}
          value={search}
        />
      </div>

      {manufacturers.length === 0 ? (
        <div className="account-enrollment-empty">
          <Text variant="bodyText">No matches</Text>
          <Text color="lightText" variant="smallText">
            No product group matches &ldquo;{search}&rdquo;.
          </Text>
        </div>
      ) : null}

      {manufacturers.map((manufacturer) => {
        const allSelected = manufacturer.groups.every((group) =>
          selectedSet.has(String(group.ProductGroupID)),
        );

        return (
          <div
            className="account-enrollment-manufacturer"
            key={manufacturer.ManufacturerID}
          >
            <SectionHeader
              rightContent={
                <CommonButton
                  onClick={() =>
                    onToggleManufacturer(manufacturer.groups, !allSelected)
                  }
                  variant="link"
                >
                  {allSelected ? "Clear all" : "Select all"}
                </CommonButton>
              }
              title={manufacturer.ManufacturerName}
            />
            <div className="account-enrollment-group-list">
              {manufacturer.groups.map((group) => {
                const checked = selectedSet.has(String(group.ProductGroupID));
                return (
                  <label
                    className={`account-enrollment-group-card ${checked ? "selected" : ""}`}
                    key={group.ProductGroupID}
                  >
                    <input
                      aria-label={group.ProductGroupName}
                      checked={checked}
                      onChange={() => onToggleGroup(group, !checked)}
                      type="checkbox"
                    />
                    <span className="account-enrollment-group-card-body">
                      <Text variant="smallText">{group.ProductGroupName}</Text>
                      <Text color="lightText" variant="tinyText">
                        {group.QCode} · {(group.Products || []).length} product
                        code{(group.Products || []).length === 1 ? "" : "s"}
                      </Text>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductGroupsStep;
